import * as turf from "@turf/turf";
import proj4 from "proj4";
import { filterCorner, PointInPolygonCheck } from "./geometry";
import { Dubins } from "dubins-js";
const EPSG5186 =
  "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=1.0 +x_0=200000 +y_0=500000 +ellps=GRS80 +units=m +no_defs";
const EPSG4326 = "+proj=longlat +datum=WGS84 +no_defs";

// 1️ 변환 (EPSG:5186 → 4326)
export function toWGS84([x, y]) {
  return proj4(EPSG5186, EPSG4326, [x, y]);
}

// 2️ 역변환 (EPSG:4326 → 5186)
export function toEPSG5186([lon, lat]) {
  return proj4(EPSG4326, EPSG5186, [lon, lat]);
}
function calculateBearing([x1, y1], [x2, y2]) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  // atan2(dx, dy)는 북쪽 기준 시계방향 각도를 라디안으로 반환
  const bearingRad = Math.atan2(dx, dy);
  // 도(degrees)로 변환
  const bearingDeg = (bearingRad * 180) / Math.PI;
  // -180~180을 0~360으로 변환
  return (bearingDeg + 360) % 360;
}

function movePoint(point, distance, bearingDegrees) {
  const bearingRad = (bearingDegrees * Math.PI) / 180 + 0;
  const x = point[0] + distance * Math.sin(bearingRad);
  const y = point[1] + distance * Math.cos(bearingRad);

  return [x, y]; // [318989.36054853833, 3978944.988053458]
}

// 스와스 연결 함수
export function connectCurves(
  forwardDist,
  current,
  next,
  pathMode,
  headlandData
) {
  const adjustedBearing = {
    p1: 50,
    p2: -10,
    p3: 180,
    p4: 10,
  };

  // 시작점 좌표 변환
  const cStartW = toWGS84(current[0]);
  // 종료점 좌표 변환 (p0)
  const cEndW = toWGS84(current.at(-1));
  // 다음 시작점 좌표 변환 (p3)
  const nStartW = toWGS84(next[0]);
  // 다음 종료점 좌표 변환
  const nEndW = toWGS84(next.at(-1));

  const bearingCurrent = turf.bearing(cStartW, cEndW); // 현재 스와스 진행 방향 산출(라디안)
  const bearingNext = turf.bearing(nStartW, nEndW); // 다음 스와스 진행 방향 산출 (라디안)

  // 진행 방향으로 forwardDistM만큼 연장
  const p1 = turf.destination(cEndW, forwardDist, bearingCurrent, {
    units: "meters",
  });
  const p2 = turf.destination(nStartW, forwardDist, bearingNext + 180, {
    units: "meters",
  });

  const bearingDegrees = calculateBearing(current.at(-1), next[0]);

  let isInside = PointInPolygonCheck(
    headlandData,
    movePoint,
    current,
    forwardDist,
    bearingDegrees
  );

  const p1_x = movePoint(
    current.at(-1),
    forwardDist / 2,
    !isInside
      ? bearingDegrees - adjustedBearing.p1
      : bearingDegrees + adjustedBearing.p1
  );
  const p2_x = movePoint(
    p1_x,
    forwardDist / 2,
    !isInside
      ? bearingDegrees + adjustedBearing.p2
      : bearingDegrees - adjustedBearing.p2
  );
  const p3_x = movePoint(
    p2_x,
    Number(forwardDist) + Number(forwardDist),
    !isInside
      ? bearingDegrees - adjustedBearing.p3
      : bearingDegrees + adjustedBearing.p3
  );
  const p4_x = movePoint(
    p3_x,
    Number(forwardDist),
    !isInside
      ? bearingDegrees + adjustedBearing.p4
      : bearingDegrees - adjustedBearing.p4
  );

  if (pathMode === "C-Turn") {
    return turf.lineString([
      current.at(-1),
      toEPSG5186(p1.geometry.coordinates),
      toEPSG5186(p2.geometry.coordinates),
      next[0],
    ]);
  } else {
    return turf.lineString([
      current.at(-1), // 스와스 끝 회전 출발
      p1_x,
      p2_x,
      p3_x,
      p4_x, // 직진 : 꼭지점 역할
      next[0], // 스와스 시작 : 회전 종료
    ]);
  }
}

// 스와스 정렬 함수
export const orderedSwaths = (finalSwaths, pathMode) => {
  const halfSwaths = Math.floor(finalSwaths.length / 2) + 1;

  const IsOdd = finalSwaths.length % 2 === 1;
  // C-Turn 모드
  // 1번 사로부터 중간 사로까지를 반복 회전하며 1사로씩 앞으로 나아간다.
  if (pathMode === "C-Turn") {
    // 1단계: 앞쪽/뒤쪽 절반으로 나누기
    // 중간 사로부터는 역방향으로 처리해 1사로부터 A - B - A - B 순서로 나아간다.
    const half = Math.floor(finalSwaths.length / 2);
    const front = finalSwaths.slice(0, half); // 1~15번
    const back = finalSwaths.slice(half, half * 2); // 16~30번
    const last = IsOdd ? [finalSwaths[finalSwaths.length - 1]] : []; // 31번 (홀수일 때)

    // 2단계: 인터리빙 (1,16,2,17,...)
    const interleaved = [];
    for (let i = 0; i < front.length; i++) {
      interleaved.push(front[i]);
      if (back[i]) interleaved.push(back[i]);
    }

    // 3단계: 마지막 스와스 추가 (홀수일 때)
    const reordered = [...interleaved, ...last];

    // 4단계: 각 스와스 방향 제어 (모드에 따라 경로 뒤집기)
    return reordered.map((s, originalIdx) => {
      const coords = s.geometry.coordinates;
      const originalIndex = finalSwaths.indexOf(s);

      // 홀수일 때 마지막 스와스는 원래 방향으로 반환
      if (IsOdd && originalIndex === finalSwaths.length - 1) {
        return coords;
      }

      if (originalIndex >= half) {
        return coords.slice().reverse();
      }
      return coords;
    });
  } else if (pathMode === "X-Turn") {
    // X-Turn 모드
    return finalSwaths.map((s, i) => {
      const coords = s.geometry.coordinates;
      return i % 2 === 0 ? coords.slice().reverse() : coords; // 홀수면 역방향
    });
  }
};

// const ensureOutsidePath = (start, end, polygon, depth = 0, maxDepth = 5) => {
//   // start, end가 일반 좌표면 geometry.coordinates로 변환
//   if (!start.geometry) {
//     start = turf.point(start);
//   }
//   if (!end.geometry) {
//     end = turf.point(end);
//   }
//   const line = turf.lineString([
//     start.geometry.coordinates,
//     end.geometry.coordinates,
//   ]);

//   const crosses =
//     turf.booleanCrosses(line, polygon) || turf.booleanWithin(line, polygon);

//   if (!crosses || depth >= maxDepth) {
//     return [start, end];
//   }

//   let mid = turf.midpoint(start, end);
//   if (turf.booleanPointInPolygon(mid, polygon)) {
//     const adjustedMid = turf.point(
//       toWGS84(
//         getExternalMidpoint(mid, polygon, start, end) // <- 네 기존 함수 재사용
//       )
//     );
//     mid = adjustedMid;
//   }
//   const left = ensureOutsidePath(start, mid, polygon, depth + 1, maxDepth);
//   const right = ensureOutsidePath(mid, end, polygon, depth + 1, maxDepth);
//   return [...left.slice(0, -1), ...right];
// };

// 중간점 외부 좌표 산출 함수

const getExternalMidpoint = (midPoint, polygon, startPoint, endPoint) => {
  const bearing = turf.bearing(startPoint, endPoint);
  const leftNormal = (bearing + 90 + 360) % 360;
  const rightNormal = (bearing - 90 + 360) % 360;

  const step = 2.5; // 2m씩 증가
  const maxOffset = 200;
  let offset = step;
  let finalMid = null;

  while (!finalMid && offset <= maxOffset) {
    const leftCandidate = turf.destination(midPoint, offset, leftNormal, {
      units: "meters",
    });
    const rightCandidate = turf.destination(midPoint, offset, rightNormal, {
      units: "meters",
    });

    const leftOutside = !turf.booleanPointInPolygon(leftCandidate, polygon);
    const rightOutside = !turf.booleanPointInPolygon(rightCandidate, polygon);

    if (leftOutside && !rightOutside) {
      finalMid = leftCandidate;
    } else if (!leftOutside && rightOutside) {
      finalMid = rightCandidate;
    } else if (leftOutside && rightOutside) {
      // 둘 다 가능하면 더 먼 쪽(바깥쪽) 선택
      const dl = turf.distance(midPoint, leftCandidate, {
        units: "meters",
      });
      const dr = turf.distance(midPoint, rightCandidate, {
        units: "meters",
      });
      finalMid = dl >= dr ? leftCandidate : rightCandidate;
    } else {
      offset += step;
    }
  }

  if (!finalMid) finalMid = midPoint;
  return toEPSG5186(finalMid.geometry.coordinates);
};

export function createCTurnPath(
  fullPath,
  turningRadius,
  headlandData,
  workArea,
  turningData,

  visualizePoints,
  scene,
  getMinZ
) {
  let startSnap = null; // 출발점에 맞닿은 회랑 좌표
  let pathSnap = null; // 경로 출발점에 맞닿은 회랑 좌표
  let pathEndSnap = null; // 경로 끝점에 맞닿은 회랑 좌표
  let startCoords = null; // [출발점, 경로 시작점] 좌표
  let pathEndCoords = null; // [경로 끝점, 출발점] 좌표

  const turningDataWithoutLast = turningData; // 닫힌 회랑 배열
  const turningDataWGS84 = turningDataWithoutLast.map((p) => toWGS84(p));
  const turningDataReverse = turningDataWGS84.reverse();
  const lineStringTurning = turf.lineString(turningDataWGS84);
  const lineStringTurningReverse = turf.lineString(turningDataReverse);

  const dubinsPath = fullPath.features.flatMap((feature, index) => {
    const coords = feature.geometry.coordinates; // 4개의 원소 배열

    // 6개 점 → X턴 처리
    if (coords.length === 6) {
      const segments = [];

      // ========== 1. p0 → p1 → p2 직각 구간 (곡선) ==========

      // p0의 진입 각도: p0→p1 방향
      const angle0 = Math.atan2(
        coords[1][1] - coords[0][1],
        coords[1][0] - coords[0][0]
      );

      // p2의 진출 각도: p1→p2 방향
      const angle2 = Math.atan2(
        coords[2][1] - coords[1][1],
        coords[2][0] - coords[1][0]
      );

      // Dubins 경로 생성
      const path02 = Dubins.path(
        { x: coords[0][0], y: coords[0][1], psi: angle0 },
        { x: coords[2][0], y: coords[2][1], psi: angle2 },
        turningRadius
      );

      const length02 = path02.length;
      const targetInterval = 1.0;
      const numSamples = Math.ceil(length02 / targetInterval);
      const clampedSamples = Math.max(5, Math.min(numSamples, 50));

      const samples02 = [];
      for (let i = 0; i <= clampedSamples; i++) {
        const pt = path02.pointAt(i / clampedSamples);
        samples02.push([pt.x, pt.y]);
      }
      segments.push(turf.lineString(samples02, { type: "curve" }));

      // ========== 2. p2 → p3 직선 (후진) ==========
      segments.push(
        turf.lineString([coords[2], coords[3]], {
          type: "straight",
          direction: "reverse",
        })
      );

      // ========== 3. p3 → p4 → p5 직각 구간 (곡선) ==========

      // p3의 진입 각도: p2→p3 방향 (또는 p3→p4 방향)
      const angle3 = Math.atan2(
        coords[4][1] - coords[3][1],
        coords[4][0] - coords[3][0]
      );

      // p5의 진출 각도: p4→p5 방향
      const angle5 = Math.atan2(
        coords[5][1] - coords[4][1],
        coords[5][0] - coords[4][0]
      );

      // Dubins 경로 생성
      const path35 = Dubins.path(
        { x: coords[3][0], y: coords[3][1], psi: angle3 },
        { x: coords[5][0], y: coords[5][1], psi: angle5 },
        turningRadius
      );

      const length35 = path35.length;
      const targetInterval35 = 1.0;
      const numSamples35 = Math.ceil(length35 / targetInterval35);
      const clampedSamples35 = Math.max(5, Math.min(numSamples35, 50));

      const samples35 = [];
      for (let i = 0; i <= clampedSamples35; i++) {
        const pt = path35.pointAt(i / clampedSamples35);
        samples35.push([pt.x, pt.y]);
      }
      segments.push(turf.lineString(samples35, { type: "curve" }));

      return segments;
    }
    // 헤드랜드 데이터 변환
    const headlandPoints = headlandData.map((p) => toWGS84(p));
    const workAreaPoints = workArea.map((p) => toWGS84(p));
    const polygon = turf.polygon([headlandPoints]);

    // 출 도착점은 필터링해서 출발점과 연결 필요
    if (
      (coords.length === 2 && index === 0) ||
      index === fullPath.features.length - 1
    ) {
      // 출발 > 시작점 / 끝점 > 출발점 배열을 대상으로 한 처리
      const coordsWGS84 = coords.map((p) => toWGS84(p)); // 스와스 라인 좌표 변환

      // 출발점 추출
      const StartsnapPoint = index === 0 ? coordsWGS84[0] : coordsWGS84[1];
      // 각각 경로 시작점과 끝점 추출
      const PathSnapPoint = index === 0 ? coordsWGS84[1] : coordsWGS84[0];

      // 시작점 근처의 회랑 좌표 추출
      const StartSnap = turf.nearestPointOnLine(
        lineStringTurning,
        StartsnapPoint
      );

      // 경로 시작점과 끝점 근처의 회랑 추출
      const PathSnap = turf.nearestPointOnLine(
        lineStringTurning,
        PathSnapPoint
      );

      if (index === 0) {
        startSnap = StartSnap; // 출발점 근처의 회랑 좌표 저장
        pathSnap = PathSnap; // 경로 시작점 인근의 회랑 좌표

        startCoords = coordsWGS84; // [출발점, 경로 시작점] 좌표 저장
      } else {
        pathEndSnap = PathSnap; // 경로 끝점 근처의 회랑 좌표 저장
        pathEndCoords = coordsWGS84; // [경로 끝점, 출발점] 좌표 저장
      }

      return [];
    }

    // 출도착점을 제외한 스와스들의 경우 변환 없이 통과
    if (coords.length < 4 && feature.properties.kind === "swath") {
      return [feature];
    }

    // 시작 , 끝 좌표 변환
    const startPoint = toWGS84(coords[0]);
    const endPoint = toWGS84(coords[3]);
    let midPoint = turf.midpoint(startPoint, endPoint);
    // 중간점 계산 ------------------------------------------------------------
    let wptMidCoord = getExternalMidpoint(
      midPoint,
      polygon,
      startPoint,
      endPoint
    );

    // 2차 중간점 계산 ------------------------------------------------------------
    // 세분화 좌/우 중간점 계산
    // const midWGS = turf.point(toWGS84(wptMidCoord));

    // // start→mid 구간 중간점
    // let leftMid = turf.midpoint(startPoint, midWGS);
    // if (turf.booleanPointInPolygon(leftMid, polygon)) {
    //   const adjustedLeft = getExternalMidpoint(
    //     leftMid,
    //     polygon,
    //     startPoint,
    //     midWGS
    //   );
    //   leftMid = turf.point(toWGS84(adjustedLeft));
    // }

    // // mid→end 구간 중간점
    // let rightMid = turf.midpoint(midWGS, endPoint);
    // if (turf.booleanPointInPolygon(rightMid, polygon)) {
    //   const adjustedRight = getExternalMidpoint(
    //     rightMid,
    //     polygon,
    //     midWGS,
    //     endPoint
    //   );
    //   rightMid = turf.point(toWGS84(adjustedRight));
    // }

    // const safePoints = [
    //   turf.point(startPoint),
    //   leftMid,
    //   midWGS,
    //   rightMid,
    //   turf.point(endPoint),
    // ];

    // const safeCoords5186 = safePoints.map((p) =>
    //   toEPSG5186(p.geometry.coordinates)
    // );
    // ------------------------------------------------------------

    // 시작 각도 계산 (coords[0] → coords[1] 방향)
    const startAngle = Math.atan2(
      coords[1][1] - coords[0][1],
      coords[1][0] - coords[0][0]
    );

    // 끝 각도 계산 (coords[2] → coords[3] 방향)
    const endAngle = Math.atan2(
      coords[3][1] - coords[2][1],
      coords[3][0] - coords[2][0]
    );

    // Waypoint 설정: 0 → 3 직접 연결
    const wpt1 = {
      x: coords[0][0], // 시작점 X
      y: coords[0][1], // 시작점 Y
      psi: startAngle, // 시작 방향
    };

    const wpt2 = {
      x: coords[3][0], // 끝점 X
      y: coords[3][1], // 끝점 Y
      psi: endAngle, // 도착 방향
    };

    // 최적의 각도 계산 로직
    let bestPsiMid = 0;
    let bestLength = Infinity;

    for (let deg = 0; deg < 360; deg += 5) {
      const psiMid = (deg * Math.PI) / 180;

      const p1 = Dubins.path(
        wpt1,
        { x: wptMidCoord[0], y: wptMidCoord[1], psi: psiMid },
        turningRadius
      );
      const p2 = Dubins.path(
        { x: wptMidCoord[0], y: wptMidCoord[1], psi: psiMid },
        wpt2,
        turningRadius
      );

      const totalLen = p1.length + p2.length;
      if (totalLen < bestLength) {
        bestLength = totalLen;
        bestPsiMid = psiMid;
      }
    }

    // 최적의 ψMid 사용
    const mid = { x: wptMidCoord[0], y: wptMidCoord[1], psi: bestPsiMid };
    const path1 = Dubins.path(wpt1, mid, turningRadius);
    const path2 = Dubins.path(mid, wpt2, turningRadius);

    // ------------------------------

    const length1 = path1.length;
    const length2 = path2.length;

    const targetInterval = 1.0;
    const numSamples1 = Math.ceil(length1 / targetInterval);
    const numSamples2 = Math.ceil(length2 / targetInterval);

    const clampedSamples1 = Math.max(5, Math.min(numSamples1, 50));
    const clampedSamples2 = Math.max(5, Math.min(numSamples2, 50));

    // 경로 샘플링
    const samples = [];
    for (let i = 0; i <= clampedSamples1; i++) {
      const point1 = path1.pointAt(i / clampedSamples1);
      samples.push([point1.x, point1.y]);
    }
    for (let i = 1; i <= clampedSamples2; i++) {
      const point2 = path2.pointAt(i / clampedSamples2);
      samples.push([point2.x, point2.y]);
    }

    return [turf.lineString(samples)];
  });

  // 회랑 인덱스 찾기
  function getSegmentIndex(lineString, point) {
    const coords = lineString.geometry.coordinates;
    let minDist = Infinity;
    let minIndex = -1;

    for (let i = 0; i < coords.length - 1; i++) {
      const seg = turf.lineString([coords[i], coords[i + 1]]);
      const snapped = turf.nearestPointOnLine(seg, point);
      if (snapped.properties.dist < minDist) {
        minDist = snapped.properties.dist;
        minIndex = i; // 세그먼트 시작 인덱스
      }
    }

    return minIndex;
  }

  if (startSnap && pathSnap && pathEndSnap) {
    const pathSegment = turf.lineSlice(startSnap, pathSnap, lineStringTurning); // 출발점에서 경로 시작까지의 회랑 좌표
    const pathSegmentReverse = turf.lineSlice(
      startSnap,
      pathSnap,
      lineStringTurningReverse
    ); // 출발점에서 경로 시작까지의 회랑 좌표

    const selected =
      turf.length(pathSegment) < turf.length(pathSegmentReverse)
        ? pathSegment
        : pathSegmentReverse;

    // const backwardSegment = getBackwardSegment(
    //   startSnap,
    //   pathSnap,
    //   lineStringTurning
    // );
    // const selected =
    //   turf.length(pathSegment) < turf.length(backwardSegment)
    //     ? pathSegment
    //     : backwardSegment;

    // ------------------------------------------------------------

    const pathEndSegment = turf.lineSlice(
      pathEndSnap,
      startSnap,
      lineStringTurning
    ); // 경로 끝점에서 출발점까지의 회랑 좌표
    const pathEndSegmentReverse = turf.lineSlice(
      pathEndSnap,
      startSnap,
      lineStringTurningReverse
    ); // 경로 끝점에서 출발점까지의 회랑 좌표
    const selectedEnd =
      turf.length(pathEndSegment) < turf.length(pathEndSegmentReverse)
        ? pathEndSegment
        : pathEndSegmentReverse;

    // const backwardEndSegment = getBackwardSegment(
    //   startSnap,
    //   pathSnap,
    //   lineStringTurning
    // );
    // const selectedEnd =
    //   turf.length(pathEndSegment) < turf.length(backwardEndSegment)
    //     ? pathEndSegment
    //     : backwardEndSegment;
    // ------------------------------------------------------------

    const selectedCoords = selected.geometry.coordinates;

    // selected의 처음, 마지막 좌표
    const first = selectedCoords[0];
    const last = selectedCoords[selectedCoords.length - 1];

    // 인덱스 재계산
    const firstIdx = getSegmentIndex(lineStringTurning, turf.point(first));
    // const lastIdx = getSegmentIndex(lineStringTurning, turf.point(last));
    const startIdx = getSegmentIndex(lineStringTurning, startSnap);

    if (firstIdx !== startIdx) {
      selectedCoords.reverse();
    }
    // let startSegment = selected.geometry.coordinates;
    // if (startIndex > endIndex) {
    //   startSegment = [...startSegment].reverse();
    // }

    // ------------------------------------------------------------

    const selectedEndCoords = selectedEnd.geometry.coordinates;
    const Endfirst = selectedEndCoords[0];
    const Endlast = selectedEndCoords[selectedEndCoords.length - 1];

    const EndfirstIdx = getSegmentIndex(
      lineStringTurning,
      turf.point(Endfirst)
    );
    // const EndlastIdx = getSegmentIndex(lineStringTurning, turf.point(Endlast));
    const EndstartIdx = getSegmentIndex(lineStringTurning, pathEndSnap);

    if (EndfirstIdx !== EndstartIdx) {
      selectedEndCoords.reverse();
    }

    // let Endsegment = selectedEnd.geometry.coordinates;
    // if (endIndex > startIndex) {
    //   Endsegment = [...Endsegment].reverse();
    // }

    const finalStartPoint = [
      startCoords[0], // 출발점
      startSnap.geometry.coordinates, // 출발점에 맞닿은 회랑 좌표
      // ...pathSegment.geometry.coordinates, // 출발점에서 경로 시작까지의 회랑 좌표
      ...selectedCoords,
      pathSnap.geometry.coordinates, // 경로 출발점에 맞닿은 회랑 좌표
      startCoords[1], // 경로 출발점
    ];

    const finalEndPoint = [
      pathEndCoords[0], // 경로 끝점
      pathEndSnap.geometry.coordinates, // 경로 끝점에 맞닿은 회랑 좌표
      // ...pathEndSegment.geometry.coordinates, // 경로 끝점에서 출발점까지의 회랑 좌표
      ...selectedEndCoords,
      startSnap.geometry.coordinates, // 출발점에 맞닿은 회랑 좌표
      pathEndCoords[1], // 경로 출발점
    ];

    const finalStartToSwath5186 = finalStartPoint.map((p) => toEPSG5186(p));
    const finalSwathToEnd5186 = finalEndPoint.map((p) => toEPSG5186(p));

    const finalStartToSwath = turf.lineString(finalStartToSwath5186);
    const finalSwathToEnd = turf.lineString(finalSwathToEnd5186);

    dubinsPath.unshift(finalStartToSwath);
    dubinsPath.push(finalSwathToEnd);
  }

  const fullDubinsPath = turf.featureCollection(dubinsPath);

  return fullDubinsPath;
}

export function createXTurnPath(
  fullPath,
  turningRadius,
  headlandData,
  workArea,
  turningData
) {
  let startSnap = null; // 출발점에 맞닿은 회랑 좌표
  let pathSnap = null; // 경로 출발점에 맞닿은 회랑 좌표
  let pathEndSnap = null; // 경로 끝점에 맞닿은 회랑 좌표
  let startCoords = null; // [출발점, 경로 시작점] 좌표
  let pathEndCoords = null; // [경로 끝점, 출발점] 좌표

  const turningDataWithoutLast = turningData; // 닫힌 회랑 배열
  const turningDataWGS84 = turningDataWithoutLast.map((p) => toWGS84(p));
  const turningDataReverse = turningDataWGS84.reverse();
  const lineStringTurning = turf.lineString(turningDataWGS84);
  const lineStringTurningReverse = turf.lineString(turningDataReverse);

  const paths = fullPath.features.flatMap((feature, index) => {
    const coords = feature.geometry.coordinates;

    if (
      (coords.length === 2 && index === 0) ||
      index === fullPath.features.length - 1
    ) {
      // 출발 > 시작점 / 끝점 > 출발점 배열을 대상으로 한 처리
      const coordsWGS84 = coords.map((p) => toWGS84(p)); // 스와스 라인 좌표 변환

      // 출발점 추출
      const StartsnapPoint = index === 0 ? coordsWGS84[0] : coordsWGS84[1];
      // 각각 경로 시작점과 끝점 추출
      const PathSnapPoint = index === 0 ? coordsWGS84[1] : coordsWGS84[0];

      // 시작점 근처의 회랑 좌표 추출
      const StartSnap = turf.nearestPointOnLine(
        lineStringTurning,
        StartsnapPoint
      );

      // 경로 시작점과 끝점 근처의 회랑 추출
      const PathSnap = turf.nearestPointOnLine(
        lineStringTurning,
        PathSnapPoint
      );

      if (index === 0) {
        startSnap = StartSnap; // 출발점 근처의 회랑 좌표 저장
        pathSnap = PathSnap; // 경로 시작점 인근의 회랑 좌표

        startCoords = coordsWGS84; // [출발점, 경로 시작점] 좌표 저장
      } else {
        pathEndSnap = PathSnap; // 경로 끝점 근처의 회랑 좌표 저장
        pathEndCoords = coordsWGS84; // [경로 끝점, 출발점] 좌표 저장
      }

      return [];
    }

    if (!coords || coords.length < 4) {
      return [feature]; // 변환 없이 통과
    }

    const segments = [];

    // ========== 1. p0 → p1 → p2 직각 구간 (곡선) ==========

    // p0의 진입 각도: p0→p1 방향
    const angle0 = Math.atan2(
      coords[1][1] - coords[0][1],
      coords[1][0] - coords[0][0]
    );

    // p2의 진출 각도: p1→p2 방향
    const angle2 = Math.atan2(
      coords[2][1] - coords[1][1],
      coords[2][0] - coords[1][0]
    );

    // Dubins 경로 생성
    const path02 = Dubins.path(
      { x: coords[0][0], y: coords[0][1], psi: angle0 },
      { x: coords[2][0], y: coords[2][1], psi: angle2 },
      turningRadius
    );

    const length02 = path02.length;
    const targetInterval = 1.0;
    const numSamples = Math.ceil(length02 / targetInterval);
    const clampedSamples = Math.max(5, Math.min(numSamples, 50));

    const samples02 = [];
    for (let i = 0; i <= clampedSamples; i++) {
      const pt = path02.pointAt(i / clampedSamples);
      samples02.push([pt.x, pt.y]);
    }
    segments.push(turf.lineString(samples02, { type: "curve" }));

    // ========== 2. p2 → p3 직선 (후진) ==========

    // p2 → p3 방향 계산
    segments.push(
      turf.lineString([coords[2], coords[3]], {
        type: "straight",
        direction: "reverse",
      })
    );

    // ========== 3. p3 → p4 → p5 직각 구간 (곡선) ==========

    // p3_extended의 진출 각도: 후진 방향
    const angle3 = Math.atan2(
      coords[4][1] - coords[3][1], // 연장된 각도
      coords[4][0] - coords[3][0]
    );

    // p5의 진출 각도: p4→p5 방향
    const angle5 = Math.atan2(
      coords[5][1] - coords[4][1],
      coords[5][0] - coords[4][0]
    );

    const correctedAngle3 = (angle3 + Math.PI) % (2 * Math.PI);

    let angleDiff = angle5 - angle3;
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    const angleDiffDeg = Math.abs((angleDiff * 180) / Math.PI);

    // Dubins 경로 생성
    const path35 = Dubins.path(
      { x: coords[3][0], y: coords[3][1], psi: angle3 },
      { x: coords[5][0], y: coords[5][1], psi: angle5 },
      turningRadius
    );

    const length35 = path35.length;
    const targetInterval35 = 1.0;
    const numSamples35 = Math.ceil(length35 / targetInterval35);
    const clampedSamples35 = Math.max(5, Math.min(numSamples35, 50));

    const samples35 = [];
    for (let i = 0; i <= clampedSamples35; i++) {
      const pt = path35.pointAt(i / clampedSamples35);
      samples35.push([pt.x, pt.y]);
    }
    segments.push(turf.lineString(samples35, { type: "curve" }));

    return segments;
  });

  // 회랑 인덱스 찾기
  function getSegmentIndex(lineString, point) {
    const coords = lineString.geometry.coordinates;
    let minDist = Infinity;
    let minIndex = -1;

    for (let i = 0; i < coords.length - 1; i++) {
      const seg = turf.lineString([coords[i], coords[i + 1]]);
      const snapped = turf.nearestPointOnLine(seg, point);
      if (snapped.properties.dist < minDist) {
        minDist = snapped.properties.dist;
        minIndex = i; // 세그먼트 시작 인덱스
      }
    }

    return minIndex;
  }

  if (startSnap && pathSnap && pathEndSnap) {
    const pathSegment = turf.lineSlice(startSnap, pathSnap, lineStringTurning); // 출발점에서 경로 시작까지의 회랑 좌표
    const pathSegmentReverse = turf.lineSlice(
      startSnap,
      pathSnap,
      lineStringTurningReverse
    ); // 출발점에서 경로 시작까지의 회랑 좌표

    const selected =
      turf.length(pathSegment) < turf.length(pathSegmentReverse)
        ? pathSegment
        : pathSegmentReverse;

    // const backwardSegment = getBackwardSegment(
    //   startSnap,
    //   pathSnap,
    //   lineStringTurning
    // );
    // const selected =
    //   turf.length(pathSegment) < turf.length(backwardSegment)
    //     ? pathSegment
    //     : backwardSegment;

    // ------------------------------------------------------------

    const pathEndSegment = turf.lineSlice(
      pathEndSnap,
      startSnap,
      lineStringTurning
    ); // 경로 끝점에서 출발점까지의 회랑 좌표
    const pathEndSegmentReverse = turf.lineSlice(
      pathEndSnap,
      startSnap,
      lineStringTurningReverse
    ); // 경로 끝점에서 출발점까지의 회랑 좌표
    const selectedEnd =
      turf.length(pathEndSegment) < turf.length(pathEndSegmentReverse)
        ? pathEndSegment
        : pathEndSegmentReverse;

    // const backwardEndSegment = getBackwardSegment(
    //   startSnap,
    //   pathSnap,
    //   lineStringTurning
    // );
    // const selectedEnd =
    //   turf.length(pathEndSegment) < turf.length(backwardEndSegment)
    //     ? pathEndSegment
    //     : backwardEndSegment;
    // ------------------------------------------------------------

    const selectedCoords = selected.geometry.coordinates;

    // selected의 처음, 마지막 좌표
    const first = selectedCoords[0];
    const last = selectedCoords[selectedCoords.length - 1];

    // 인덱스 재계산
    const firstIdx = getSegmentIndex(lineStringTurning, turf.point(first));
    // const lastIdx = getSegmentIndex(lineStringTurning, turf.point(last));
    const startIdx = getSegmentIndex(lineStringTurning, startSnap);

    if (firstIdx !== startIdx) {
      selectedCoords.reverse();
    }
    // let startSegment = selected.geometry.coordinates;
    // if (startIndex > endIndex) {
    //   startSegment = [...startSegment].reverse();
    // }

    // ------------------------------------------------------------

    const selectedEndCoords = selectedEnd.geometry.coordinates;
    const Endfirst = selectedEndCoords[0];
    const Endlast = selectedEndCoords[selectedEndCoords.length - 1];

    const EndfirstIdx = getSegmentIndex(
      lineStringTurning,
      turf.point(Endfirst)
    );
    // const EndlastIdx = getSegmentIndex(lineStringTurning, turf.point(Endlast));
    const EndstartIdx = getSegmentIndex(lineStringTurning, pathEndSnap);

    if (EndfirstIdx !== EndstartIdx) {
      selectedEndCoords.reverse();
    }

    // let Endsegment = selectedEnd.geometry.coordinates;
    // if (endIndex > startIndex) {
    //   Endsegment = [...Endsegment].reverse();
    // }

    const finalStartPoint = [
      startCoords[0], // 출발점
      startSnap.geometry.coordinates, // 출발점에 맞닿은 회랑 좌표
      // ...pathSegment.geometry.coordinates, // 출발점에서 경로 시작까지의 회랑 좌표
      ...selectedCoords,
      pathSnap.geometry.coordinates, // 경로 출발점에 맞닿은 회랑 좌표
      startCoords[1], // 경로 출발점
    ];

    const finalEndPoint = [
      pathEndCoords[0], // 경로 끝점
      pathEndSnap.geometry.coordinates, // 경로 끝점에 맞닿은 회랑 좌표
      // ...pathEndSegment.geometry.coordinates, // 경로 끝점에서 출발점까지의 회랑 좌표
      ...selectedEndCoords,
      startSnap.geometry.coordinates, // 출발점에 맞닿은 회랑 좌표
      pathEndCoords[1], // 경로 출발점
    ];

    const finalStartToSwath5186 = finalStartPoint.map((p) => toEPSG5186(p));
    const finalSwathToEnd5186 = finalEndPoint.map((p) => toEPSG5186(p));

    const finalStartToSwath = turf.lineString(finalStartToSwath5186);
    const finalSwathToEnd = turf.lineString(finalSwathToEnd5186);

    paths.unshift(finalStartToSwath);
    paths.push(finalSwathToEnd);
  }

  return turf.featureCollection(paths);
}

// 전체 경로 생성 함수
export function FullPathGeneration(
  swaths,
  forwardDist,
  turningRadius = 3.5,
  numSamples = 32,
  pathMode,
  headlandWidth,
  headlandData,
  selectedStartPoint
) {
  const pathSegments = [];
  const totalTurns = swaths.length - 1;

  for (let i = 0; i < swaths.length - 1; i++) {
    const current = swaths[i]; // 현재 스와스
    const next = swaths[i + 1]; // 다음 스와스
    // i==0일 때만 current 스와스 선 추가
    if (i === 0) {
      const swathCurrent = turf.lineString(current, { kind: "swath" });
      pathSegments.push(swathCurrent);
    }

    const isLastTurn = i === totalTurns - 1;
    const isOddTurn = i % 2 === 1;

    let turn;

    // let turn = connectCurves(forwardDist, current, next, pathMode);

    // C턴 모드이면서 마지막 홀수 턴 → X턴 방식 적용
    if (pathMode === "C-Turn" && isLastTurn && isOddTurn) {
      // X턴 좌표 생성 (6개 점)
      turn = connectCurves(
        headlandWidth,
        current,
        next,
        "X-Turn",
        headlandData
      );
    } else {
      // 기본 처리
      turn = connectCurves(forwardDist, current, next, pathMode, headlandData);
    }

    pathSegments.push(turn);

    const swathNext = turf.lineString(next, { kind: "swath" });
    pathSegments.push(swathNext);
  }

  const start = [selectedStartPoint.x, selectedStartPoint.y];
  const front = pathSegments[0].geometry.coordinates[0];
  const back = pathSegments.at(-1).geometry.coordinates.at(-1);

  const distance = ([a, b], [c, d]) => {
    return Math.sqrt((a - c) ** 2 + (b - d) ** 2);
  };
  const connectFront = distance(start, front) <= distance(start, back);

  if (connectFront) {
    const entry = turf.lineString([start, front]);
    const exit = turf.lineString([back, start]);
    if (distance(start, front) > 1e-6) pathSegments.unshift(entry);
    if (distance(back, start) > 1e-6) pathSegments.push(exit);
  } else {
    pathSegments.reverse();

    // 각 segment의 좌표도 reverse
    pathSegments.forEach((segment) => {
      if (segment.geometry && segment.geometry.coordinates) {
        segment.geometry.coordinates.reverse();
      }
    });

    const entry = turf.lineString([start, back]);
    const exit = turf.lineString([front, start]);
    if (distance(start, back) > 1e-6) pathSegments.unshift(entry);
    if (distance(front, start) > 1e-6) pathSegments.push(exit);
  }

  const fullPath = turf.featureCollection(pathSegments);
  return fullPath;
}
